import {
  keepPreviousData,
  skipToken,
  useInfiniteQuery,
  useQuery,
  type UseQueryOptions,
} from '@tanstack/react-query';

import { fetchClient } from '@/lib/openapi-fetch/fetchClient';
import { candidateEndpoints } from '@/modules/candidates/constants';
import type {
  CandidateJobsPageApi,
  CandidateJobsQueryParams,
} from '@/modules/candidates/types';

import { candidateJobQueryKeys } from './queryKeys';

/** API maximum used by recruiter and debug views that intentionally load one large page. */
export const CANDIDATE_JOBS_MAX_LIMIT = 500;

const HM_CANDIDATE_JOBS_PAGE_SIZE = 15;

type CandidateJobsFilters = Omit<CandidateJobsQueryParams, 'cursor' | 'limit'>;

export const useCandidateJobsQuery = (
  {
    jobId,
    ...filters
  }: CandidateJobsQueryParams & { jobId: string | undefined },
  options?: Omit<
    UseQueryOptions<CandidateJobsPageApi | undefined, unknown>,
    'queryKey' | 'queryFn'
  >,
) => {
  return useQuery({
    queryKey: candidateJobQueryKeys.list(jobId, filters),
    queryFn: jobId
      ? async () => {
          const { data } = await fetchClient.GET(
            candidateEndpoints.candidateJobs,
            {
              params: {
                path: { jobId },
                query: filters,
              },
            },
          );

          return data;
        }
      : skipToken,
    ...options,
  });
};

/** Loads cursor pages of candidate jobs while retaining the selected filters. */
export function useInfiniteCandidateJobsQuery(
  {
    jobId,
    enabled = true,
    ...filters
  }: CandidateJobsFilters & { enabled?: boolean; jobId: string | undefined },
  limit = HM_CANDIDATE_JOBS_PAGE_SIZE,
) {
  const query = useInfiniteQuery({
    queryKey: candidateJobQueryKeys.infiniteList(jobId, filters),
    initialPageParam: undefined as string | undefined,
    queryFn:
      jobId && enabled
        ? async ({ pageParam }) => {
            const { data } = await fetchClient.GET(
              candidateEndpoints.candidateJobs,
              {
                params: {
                  path: { jobId },
                  query: {
                    ...filters,
                    cursor: pageParam,
                    limit,
                  },
                },
              },
            );

            return data;
          }
        : skipToken,
    // A new filter (a search) keeps the rows on screen until its first page
    // lands, so the table and its selection stay mounted. Disabled lists
    // must not inherit another filter's counts.
    placeholderData: enabled ? keepPreviousData : undefined,
    enabled,
    getNextPageParam: (lastPage) => lastPage?.nextCursor ?? undefined,
  });

  return {
    ...query,
    candidates: query.data?.pages.flatMap((page) => page?.items ?? []) ?? [],
    totalCount: query.data?.pages[0]?.metadata.totalCount,
  };
}
